import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { HoverHintContent, HoverHintWithGlyph } from "@/components/ui/title-help";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ApiError, apiFetch } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { buildSetBomRulesDeepLink } from "@/lib/bomDeepLink";
import { cn } from "@/lib/utils";
import {
  ProductionCostReportPeriodPicker,
  type ProductionCostReportPeriod,
} from "@/pages/manufacturing/ProductionCostReportPeriodPicker";

interface ProductionCostStationCostRow {
  stationNumber: number;
  key: string;
  label: string;
  costPerUnit: number | null;
  avgMinutes: number;
  blendedDollarsPerHour: number | null;
  unitCount: number;
  timeBreakdown: string;
}

interface ProductionCostBomComponentRow {
  slotName: string;
  exampleSku: string | null;
  variableName: string | null;
  quantityPerUnit: number;
  unitPrice: number | null;
  costPerUnit: number | null;
  priceSource:
    | "sku_unit_cost"
    | "bom_roll_up"
    | "canonical_bom"
    | "finished_good_roll_up"
    | "missing";
}

interface ProductionCostBomIssue {
  sku: string;
  message: string;
}

interface ProductionCostProductLineRow {
  key: string;
  label: string;
  bomCostPerUnit: number | null;
  bomWorkOrderCount: number;
  bomComponents: ProductionCostBomComponentRow[];
  bomPricingComplete: boolean;
  bomIssues: ProductionCostBomIssue[];
  /** True while SHP material BOM is still loading on a separate request. */
  bomMaterialPending: boolean;
  stations: ProductionCostStationCostRow[];
  laborCostPerUnit: number | null;
  totalCostPerUnit: number | null;
  laborRatesConfigured: boolean;
}

interface ProductionCostBomOnlyProductLine {
  key: string;
  bomCostPerUnit: number | null;
  bomWorkOrderCount: number;
  bomComponents: ProductionCostBomComponentRow[];
  bomPricingComplete: boolean;
  bomIssues: ProductionCostBomIssue[];
}

interface ProductionCostBomOnlyResponse {
  startDate: string;
  endDate: string;
  productLines: ProductionCostBomOnlyProductLine[];
}

interface ProductionCostUtilizationDayRow {
  date: string;
  scheduledMinutes: number;
  billableMinutes: number;
  utilizationPercent: number | null;
}

interface ProductionCostUtilizationStaffRow {
  label: string;
  scheduledMinutes: number;
  billableMinutes: number;
  utilizationPercent: number | null;
}

interface FloorLaborStationShare {
  key: string;
  label: string;
  minutes: number;
  percentPoints: number | null;
}

interface FloorLaborPersonShare {
  key: string;
  label: string;
  minutes: number;
}

interface FloorLaborEfficiencyWeekRow {
  startDate: string;
  endDate: string;
  label: string;
  qbtPaidMinutes: number;
  billableMinutes: number;
  productiveOverheadMinutes: number;
  unproductiveMinutes: number;
  untrackedMinutes: number;
  billablePercent: number | null;
  productivePercent: number | null;
  unproductivePercent: number | null;
  untrackedPercent: number | null;
  qbtPaidByPerson: FloorLaborPersonShare[];
  billableByPerson: FloorLaborPersonShare[];
}

interface FloorLaborEfficiencyReport {
  qbtDenominatorLabels: string[];
  qbtStatus: "not_configured" | "ok" | "error";
  qbtError: string | null;
  weeks: FloorLaborEfficiencyWeekRow[];
  totals: {
    qbtPaidMinutes: number;
    billableMinutes: number;
    productiveOverheadMinutes: number;
    unproductiveMinutes: number;
    untrackedMinutes: number;
    billablePercent: number | null;
    productivePercent: number | null;
    unproductivePercent: number | null;
    untrackedPercent: number | null;
    billableByStation: FloorLaborStationShare[];
    productiveByStation: FloorLaborStationShare[];
    unproductiveByStation: FloorLaborStationShare[];
    untrackedByActivity: FloorLaborStationShare[];
  };
}

interface ProductionCostLaborRateMonthRow {
  yearMonth: string;
  laborDollarsPerHour: number | null;
  overheadDollarsPerHour: number | null;
  combinedDollarsPerHour: number | null;
  combinedDollarsPerMinute: number | null;
}

interface ProductionCostLaborRates {
  configured: boolean;
  blendedDollarsPerHour: number | null;
  blendedDollarsPerMinute: number | null;
  months: ProductionCostLaborRateMonthRow[];
}

interface ProductionCostReportResponse {
  timeZone: string;
  targetUtilizationPercent: number;
  weekCount: number;
  startDate: string;
  endDate: string;
  laborRates: ProductionCostLaborRates;
  utilization: {
    days: ProductionCostUtilizationDayRow[];
    totals: {
      scheduledMinutes: number;
      billableMinutes: number;
      utilizationPercent: number | null;
    };
    staff: ProductionCostUtilizationStaffRow[];
  };
  productLines: ProductionCostProductLineRow[];
  floorLaborEfficiency: FloorLaborEfficiencyReport;
}

function chicagoDateToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  return `${year}-${month}-${day}`;
}

function defaultPreviousFourWeeks(): ProductionCostReportPeriod {
  const today = chicagoDateToday();
  const anchor = new Date(`${today}T12:00:00`);
  const weekday = anchor.getDay();
  const daysFromMonday = (weekday + 6) % 7;
  const thisMonday = new Date(anchor);
  thisMonday.setDate(anchor.getDate() - daysFromMonday);
  const lastSunday = new Date(thisMonday);
  lastSunday.setDate(thisMonday.getDate() - 1);
  const firstMonday = new Date(lastSunday);
  firstMonday.setDate(lastSunday.getDate() - 27);
  const format = (value: Date) =>
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  return { startDate: format(firstMonday), endDate: format(lastSunday) };
}

function formatMinutesAsHours(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${hours}:${String(remainder).padStart(2, "0")}`;
}

function formatPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function formatCurrency(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatReportDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function utilizationTone(percent: number | null, targetPercent: number): string {
  if (percent == null) {
    return "text-slate-500";
  }
  if (percent >= targetPercent) {
    return "text-emerald-700";
  }
  if (percent >= targetPercent * 0.9) {
    return "text-amber-700";
  }
  return "text-rose-700";
}

function CostCell({
  value,
  hint,
  pending = false,
}: {
  value: number | null;
  hint: ReactNode;
  pending?: boolean;
}) {
  if (pending) {
    return (
      <span
        className="inline-block select-none blur-sm filter motion-safe:animate-pulse"
        aria-busy="true"
        aria-label="Loading"
        title="Loading material cost"
      >
        <span className="font-mono tabular-nums text-slate-400">$0.00</span>
      </span>
    );
  }
  return (
    <HoverHintContent hoverHint={hint} block>
      <span className="font-mono tabular-nums text-slate-900">{formatCurrency(value)}</span>
    </HoverHintContent>
  );
}

function mergeBomIntoProductLines(
  lines: ProductionCostProductLineRow[],
  bomByKey: Map<string, ProductionCostBomOnlyProductLine> | null,
  bomPending: boolean,
): ProductionCostProductLineRow[] {
  return lines.map((line) => {
    if (bomPending || bomByKey == null) {
      return {
        ...line,
        bomCostPerUnit: null,
        bomWorkOrderCount: 0,
        bomComponents: [],
        bomPricingComplete: false,
        bomIssues: [],
        bomMaterialPending: true,
        totalCostPerUnit: null,
      };
    }
    const bom = bomByKey.get(line.key);
    if (bom == null) {
      return {
        ...line,
        bomMaterialPending: false,
        totalCostPerUnit: line.laborCostPerUnit,
      };
    }
    const bomCostPerUnit = bom.bomCostPerUnit;
    const laborCostPerUnit = line.laborCostPerUnit;
    const totalCostPerUnit =
      laborCostPerUnit != null && bomCostPerUnit != null
        ? Math.round((laborCostPerUnit + bomCostPerUnit) * 100) / 100
        : laborCostPerUnit != null && bomCostPerUnit == null
          ? laborCostPerUnit
          : bomCostPerUnit != null && laborCostPerUnit == null
            ? bomCostPerUnit
            : null;
    return {
      ...line,
      bomCostPerUnit,
      bomWorkOrderCount: bom.bomWorkOrderCount,
      bomComponents: bom.bomComponents,
      bomPricingComplete: bom.bomPricingComplete,
      bomIssues: bom.bomIssues,
      bomMaterialPending: false,
      totalCostPerUnit,
    };
  });
}

function formatBomPriceSourceLabel(source: ProductionCostBomComponentRow["priceSource"]): string {
  switch (source) {
    case "sku_unit_cost":
      return "Megaventory unit cost";
    case "bom_roll_up":
      return "Subassembly roll-up";
    case "canonical_bom":
      return "Canonical board BOM";
    case "finished_good_roll_up":
      return "Finished good (material + work-order labor)";
    default:
      return "Missing price";
  }
}

function BomCostHoverHint({
  line,
}: {
  line: ProductionCostProductLineRow;
}): ReactNode {
  if (line.bomWorkOrderCount === 0) {
    return line.key === "clamp_qc"
      ? "No QC work orders with complete Kodiak / SGS BoM pricing matched the 90-002* / 90-003* clamp lines in the window."
      : "No received work orders with complete Kodiak / SGS TLA BoM pricing matched this product family in the window.";
  }

  const workOrderLabel =
    line.key === "clamp_qc"
      ? `${line.bomWorkOrderCount.toLocaleString()} QC work order${line.bomWorkOrderCount === 1 ? "" : "s"} with hub inspections`
      : `${line.bomWorkOrderCount.toLocaleString()} received work order${line.bomWorkOrderCount === 1 ? "" : "s"}`;

  const materialCopy =
    line.key === "clamp_qc"
      ? `The dollar total is the purchased coated-clamp finished-good Megaventory unit cost (90-002-04 / 90-003-04), not a TLA family average. Average across ${workOrderLabel} in this window.`
      : `The dollar total is the unweighted average of nonzero Kodiak ALT TLA Megaventory unit costs for this product line. FI-3C averages A and C when both have cost (for example ALT-FI-3C-N-TLA-A and ALT-FI-3C-N-TLA-C). FI-5A uses the same rule on ALT-FI-5A-N-TLA / ALT-FI-5A-W-TLA, including later letter variants when they have cost. Each unit cost already includes TLA labor, TLA BoM, PCBA labor, and PCBA BoM. Rows below are the exploded composition for inspection; they are not added on top of that average. Suntronic 88-025 labor and kit slots (fiber, box, labels, adapters) are omitted. Suntronic equivalent costs are not in this report yet. Average across ${workOrderLabel} in this window.`;

  return (
    <div className="max-w-[22rem] space-y-2">
      <div className="text-xs text-slate-600">
        {materialCopy}
        {!line.bomPricingComplete ? (
          <span className="mt-1 block text-amber-800">
            Partial pricing: some exploded TLA lines lack Megaventory unit costs. That does not
            change the total, which is the Kodiak TLA family average when present.
          </span>
        ) : null}
        {line.bomIssues.length > 0 ? (
          <span className="mt-1 block text-amber-800">
            Hub BoM issues for {line.bomIssues.map((issue) => issue.sku).join(", ")}. Open Set BoM rules from the
            product-line banner to fix parse or template data.
          </span>
        ) : null}
      </div>
      {line.bomComponents.length > 0 ? (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {line.key === "clamp_qc"
              ? "Purchased finished-good composition"
              : "Exploded composition (already in TLA family average)"}
          </p>
          <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
              <th className="pb-1 pr-2 font-semibold">Slot</th>
              <th className="pb-1 pr-2 text-right font-semibold">Qty</th>
              <th className="pb-1 pr-2 text-right font-semibold">Unit</th>
              <th className="pb-1 text-right font-semibold">Cost</th>
            </tr>
          </thead>
          <tbody>
            {line.bomComponents.map((row) => (
              <tr
                key={`${row.slotName}-${row.quantityPerUnit}`}
                className="border-t border-slate-100"
              >
                <td className="py-1 pr-2">
                  <div className="font-medium text-slate-800">{row.slotName}</div>
                  {row.exampleSku ? (
                    <div className="text-[10px] text-slate-500">{row.exampleSku}</div>
                  ) : (
                    <div className="text-[10px] text-slate-500">SKU varies by configuration</div>
                  )}
                  <div className="text-[10px] text-slate-500">{formatBomPriceSourceLabel(row.priceSource)}</div>
                </td>
                <td className="py-1 pr-2 text-right tabular-nums">{row.quantityPerUnit}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{formatCurrency(row.unitPrice)}</td>
                <td className="py-1 text-right tabular-nums font-medium text-slate-800">
                  {formatCurrency(row.costPerUnit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : null}
    </div>
  );
}

function formatPerMinuteRate(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPerHourRate(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function LaborRateHeaderHoverHint({ laborRates }: { laborRates: ProductionCostLaborRates }): ReactNode {
  if (!laborRates.configured) {
    return (
      <span>
        Internal labor plus overhead rates are missing for at least one calendar month in this window. Set monthly
        rates in Manufacturing → Settings.
      </span>
    );
  }
  return (
    <div className="max-w-[20rem] space-y-2">
      <div className="text-xs text-slate-600">
        Floor minutes in station cost columns are valued at the monthly internal labor plus overhead rate for the scan
        month (labor $/hr + overhead $/hr, blended per minute).
      </div>
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500">
            <th className="pb-1 pr-2 font-semibold">Month</th>
            <th className="pb-1 pr-2 text-right font-semibold">Labor/hr</th>
            <th className="pb-1 pr-2 text-right font-semibold">Overhead/hr</th>
            <th className="pb-1 pr-2 text-right font-semibold">Combined/hr</th>
            <th className="pb-1 text-right font-semibold">Combined/min</th>
          </tr>
        </thead>
        <tbody>
          {laborRates.months.map((row) => (
            <tr key={row.yearMonth} className="border-t border-slate-100">
              <td className="py-1 pr-2 font-medium text-slate-800">{row.yearMonth}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{formatPerHourRate(row.laborDollarsPerHour)}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{formatPerHourRate(row.overheadDollarsPerHour)}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{formatPerHourRate(row.combinedDollarsPerHour)}</td>
              <td className="py-1 text-right tabular-nums font-medium text-slate-800">
                {formatPerMinuteRate(row.combinedDollarsPerMinute)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LaborRateHeaderBadge({ laborRates }: { laborRates: ProductionCostLaborRates }): ReactNode {
  const perMinute = laborRates.blendedDollarsPerMinute;
  const perHour = laborRates.blendedDollarsPerHour;
  const label =
    perMinute != null
      ? `${formatPerMinuteRate(perMinute)}/min`
      : perHour != null
        ? `${formatPerHourRate(perHour)}/hr`
        : "Rates missing";

  return (
    <HoverHintContent hoverHint={<LaborRateHeaderHoverHint laborRates={laborRates} />} align="center">
      <div className="text-right text-sm text-slate-700">
        <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Internal labor rate</div>
        <div className="font-mono text-base font-semibold tabular-nums text-slate-900">{label}</div>
        {perMinute != null && perHour != null ? (
          <div className="text-[11px] tabular-nums text-slate-500">{formatPerHourRate(perHour)}/hr blended</div>
        ) : null}
      </div>
    </HoverHintContent>
  );
}

function UtilizationStaffHoverHint({
  staff,
  targetPercent,
}: {
  staff: ProductionCostUtilizationStaffRow[];
  targetPercent: number;
}): ReactNode {
  if (staff.length === 0) {
    return <span>No staff-level utilization in this window.</span>;
  }
  return (
    <div className="w-max max-w-[min(36rem,calc(100vw-2rem))] space-y-2">
      <div className="font-medium text-slate-800">By staff member</div>
      <table className="w-max text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500">
            <th className="pb-1 pr-3 font-semibold">Staff</th>
            <th className="pb-1 pr-3 text-right font-semibold">Billable</th>
            <th className="pb-1 pr-3 text-right font-semibold">Scheduled</th>
            <th className="pb-1 text-right font-semibold">Util</th>
          </tr>
        </thead>
        <tbody>
          {staff.map((row) => (
            <tr key={`${row.label}-${row.billableMinutes}-${row.scheduledMinutes}`} className="border-t border-slate-100">
              <td className="whitespace-nowrap py-1 pr-3 font-medium text-slate-800">{row.label}</td>
              <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">
                {formatMinutesAsHours(row.billableMinutes)}
              </td>
              <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">
                {formatMinutesAsHours(row.scheduledMinutes)}
              </td>
              <td
                className={cn(
                  "whitespace-nowrap py-1 text-right tabular-nums font-medium",
                  utilizationTone(row.utilizationPercent, targetPercent),
                )}
              >
                {formatPercent(row.utilizationPercent)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatPercentPoints(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toFixed(1)} pts`;
}

function FloorLaborStationShareHoverHint({
  title,
  intro,
  rows,
  emptyMessage,
}: {
  title: string;
  intro: string;
  rows: FloorLaborStationShare[];
  emptyMessage: string;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <div className="max-w-[18rem] space-y-1.5">
        <div className="font-medium text-slate-800">{title}</div>
        <p>{intro}</p>
        <p className="text-slate-500">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div className="max-w-[18rem] space-y-2">
      <div className="font-medium text-slate-800">{title}</div>
      <p>{intro}</p>
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500">
            <th className="pb-1 pr-3 font-semibold">Activity</th>
            <th className="pb-1 pr-3 text-right font-semibold">Time</th>
            <th className="pb-1 text-right font-semibold">Pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-slate-100">
              <td className="py-1 pr-3 font-medium text-slate-800">{row.label}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{formatMinutesAsHours(row.minutes)}</td>
              <td className="py-1 text-right tabular-nums font-medium text-slate-900">
                {formatPercentPoints(row.percentPoints)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FloorLaborDefinitionsHoverHint(): ReactNode {
  const sections = [
    {
      title: "Billable",
      body: "Hub floor time that can be attributed to a device for unit cost: clamp QC, outbound pre-pack/shipping, and scan time linked to a batch, shipment, or work order.",
    },
    {
      title: "Productive",
      body: "Adds useful overhead that is not billable to a device (transfer-pack and mobile Transfers).",
    },
    {
      title: "Unproductive",
      body: "Other clocked hub and mobile time that is not billable to a device and not productive overhead. Includes supervisor time added on Daily task (Add misc time), Break, Receiving, Misc, leftover station time (Config, Cal+Test, and similar), and legacy mobile Clamp Building / Clamp QC.",
    },
    {
      title: "Untracked",
      body: "QuickBooks Time paid time with no hub or mobile clock at all. Supervisor Daily task entries are a clock, so they count as unproductive, not untracked.",
    },
    {
      title: "Denominator",
      body: "QuickBooks Time paid hours for Daniel Bakos, Melissa Mastan-Stanfield, and Gary Moore (regular and manual time only).",
    },
    {
      title: "Numerator",
      body: "All staff, so percentages can exceed 100%.",
    },
  ];
  return (
    <div className="w-max max-w-[min(24rem,calc(100vw-2rem))] space-y-2.5 text-xs leading-snug">
      {sections.map((section) => (
        <div key={section.title}>
          <div className="font-semibold text-slate-800">{section.title}</div>
          <p className="mt-0.5 text-slate-700">{section.body}</p>
        </div>
      ))}
    </div>
  );
}

function FloorLaborMetricValue({
  value,
  detail,
  hint,
}: {
  value: string;
  detail: ReactNode;
  hint: ReactNode;
}): ReactNode {
  return (
    <HoverHintContent block hoverHint={hint}>
      <div className="flex flex-col gap-1">
        <span className="text-2xl font-semibold tabular-nums text-slate-900">{value}</span>
        <span className="text-xs text-slate-500">{detail}</span>
      </div>
    </HoverHintContent>
  );
}

function FloorLaborPersonShareHoverHint({
  title,
  intro,
  rows,
  emptyMessage,
}: {
  title: string;
  intro: string;
  rows: FloorLaborPersonShare[];
  emptyMessage: string;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <div className="max-w-[18rem] space-y-1.5">
        <div className="font-medium text-slate-800">{title}</div>
        <p>{intro}</p>
        <p className="text-slate-500">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div className="max-w-[18rem] space-y-2">
      <div className="font-medium text-slate-800">{title}</div>
      <p>{intro}</p>
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500">
            <th className="pb-1 pr-3 font-semibold">Person</th>
            <th className="pb-1 text-right font-semibold">Time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-slate-100">
              <td className="py-1 pr-3 font-medium text-slate-800">{row.label}</td>
              <td className="py-1 text-right tabular-nums font-medium text-slate-900">
                {formatMinutesAsHours(row.minutes)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProductionCostReportTab({ refreshGeneration = 0 }: { refreshGeneration?: number }) {
  const queryClient = useQueryClient();
  const defaultWindow = useMemo(() => defaultPreviousFourWeeks(), []);
  const [applied, setApplied] = useState<ProductionCostReportPeriod>(defaultWindow);
  const windowKey = `${applied.startDate}|${applied.endDate}`;

  const query = useQuery<ProductionCostReportResponse, ApiError>({
    queryKey: ["manufacturing", "reporting", "production-cost", applied.startDate, applied.endDate],
    queryFn: () =>
      apiFetch<ProductionCostReportResponse>("/api/manufacturing/reporting/production-cost", {
        query: {
          startDate: applied.startDate,
          endDate: applied.endDate,
          background: "1",
        },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const bomQuery = useQuery<ProductionCostBomOnlyResponse, ApiError>({
    queryKey: ["manufacturing", "reporting", "production-cost", "bom", applied.startDate, applied.endDate],
    queryFn: () =>
      apiFetch<ProductionCostBomOnlyResponse>("/api/manufacturing/reporting/production-cost/bom", {
        query: {
          startDate: applied.startDate,
          endDate: applied.endDate,
          background: "1",
        },
      }),
    enabled: query.isSuccess,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [
      HUB_CACHE_CHANNEL.manufacturingReportingProductionCost(windowKey),
      HUB_CACHE_CHANNEL.manufacturingReportingProductionCostBom(windowKey),
    ],
    onEvent: (event) => {
      if (event.phase !== "ready" && event.phase !== "invalidated") {
        return;
      }
      if (event.channel === HUB_CACHE_CHANNEL.manufacturingReportingProductionCost(windowKey)) {
        void queryClient.invalidateQueries({
          queryKey: ["manufacturing", "reporting", "production-cost", applied.startDate, applied.endDate],
        });
      }
      if (event.channel === HUB_CACHE_CHANNEL.manufacturingReportingProductionCostBom(windowKey)) {
        void queryClient.invalidateQueries({
          queryKey: ["manufacturing", "reporting", "production-cost", "bom", applied.startDate, applied.endDate],
        });
      }
    },
  });

  useEffect(() => {
    if (refreshGeneration <= 0) {
      return;
    }
    void queryClient.fetchQuery({
      queryKey: ["manufacturing", "reporting", "production-cost", applied.startDate, applied.endDate],
      queryFn: () =>
        apiFetch<ProductionCostReportResponse>("/api/manufacturing/reporting/production-cost", {
          query: {
            startDate: applied.startDate,
            endDate: applied.endDate,
            refresh: "1",
          },
        }),
    });
    void queryClient.fetchQuery({
      queryKey: ["manufacturing", "reporting", "production-cost", "bom", applied.startDate, applied.endDate],
      queryFn: () =>
        apiFetch<ProductionCostBomOnlyResponse>("/api/manufacturing/reporting/production-cost/bom", {
          query: {
            startDate: applied.startDate,
            endDate: applied.endDate,
            refresh: "1",
          },
        }),
    });
  }, [applied.endDate, applied.startDate, queryClient, refreshGeneration]);

  const data = query.data;
  const bomByKey = useMemo(() => {
    if (bomQuery.data == null) {
      return null;
    }
    return new Map(bomQuery.data.productLines.map((line) => [line.key, line]));
  }, [bomQuery.data]);
  const bomMaterialPending = query.isSuccess && !bomQuery.isSuccess && !bomQuery.isError;
  const productLines = useMemo(
    () => mergeBomIntoProductLines(data?.productLines ?? [], bomByKey, bomMaterialPending),
    [data?.productLines, bomByKey, bomMaterialPending],
  );
  const utilizationRows = useMemo(() => [...(data?.utilization.days ?? [])].reverse(), [data?.utilization.days]);
  const targetPercent = data?.targetUtilizationPercent ?? 80;

  return (
    <div className="space-y-8">
      <ProductionCostReportPeriodPicker
        value={applied}
        onChange={setApplied}
        previousFourWeeks={defaultPreviousFourWeeks}
      />

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">Billable and productive labor</h2>
          <HoverHintWithGlyph
            hoverHintAriaLabel="How billable and productive labor is calculated"
            hoverHintContent={<FloorLaborDefinitionsHoverHint />}
          />
        </div>

        {query.isLoading ? (
          <p className="py-4 text-center text-sm text-slate-500">Loading floor labor metrics...</p>
        ) : query.error ? null : data == null ? null : data.floorLaborEfficiency.qbtStatus === "error" ? (
          <p className="text-sm text-rose-700">{data.floorLaborEfficiency.qbtError ?? "Could not load QuickBooks Time denominator."}</p>
        ) : data.floorLaborEfficiency.qbtStatus === "not_configured" ? (
          <p className="text-sm text-amber-800">
            Connect QuickBooks Time to compute billable and productive percentages against paid supervisor hours.
          </p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Card>
                <CardBody className="flex flex-col gap-1 px-3 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Billable %</span>
                  <FloorLaborMetricValue
                    value={formatPercent(data.floorLaborEfficiency.totals.billablePercent)}
                    detail={
                      <>
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.billableMinutes)} billable /{" "}
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.qbtPaidMinutes)} QBT paid
                      </>
                    }
                    hint={
                      <FloorLaborStationShareHoverHint
                        title="Billable % by activity"
                        intro="Hub floor time that can be attributed to a device for unit cost, as percentage points of QBT paid."
                        rows={data.floorLaborEfficiency.totals.billableByStation}
                        emptyMessage="No billable station time in this window."
                      />
                    }
                  />
                </CardBody>
              </Card>
              <Card>
                <CardBody className="flex flex-col gap-1 px-3 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Productive %</span>
                  <FloorLaborMetricValue
                    value={formatPercent(data.floorLaborEfficiency.totals.productivePercent)}
                    detail={
                      <>
                        Includes {formatMinutesAsHours(data.floorLaborEfficiency.totals.productiveOverheadMinutes)}{" "}
                        productive overhead (transfer-pack, transfers)
                      </>
                    }
                    hint={
                      <FloorLaborStationShareHoverHint
                        title="Productive % by activity"
                        intro="Billable station time plus productive overhead (transfer-pack and mobile Transfers), as percentage points of QBT paid."
                        rows={data.floorLaborEfficiency.totals.productiveByStation}
                        emptyMessage="No productive station time in this window."
                      />
                    }
                  />
                </CardBody>
              </Card>
              <Card>
                <CardBody className="flex flex-col gap-1 px-3 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Unproductive %</span>
                  <FloorLaborMetricValue
                    value={formatPercent(data.floorLaborEfficiency.totals.unproductivePercent)}
                    detail={
                      <>
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.unproductiveMinutes)} unproductive /{" "}
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.qbtPaidMinutes)} QBT paid
                      </>
                    }
                    hint={
                      <FloorLaborStationShareHoverHint
                        title="Unproductive % by activity"
                        intro="Clocked hub and mobile time that is neither billable to a device nor productive overhead, as percentage points of QBT paid. Includes supervisor time added on Daily task (Add misc time), operator Break, and legacy mobile Clamp Building / Clamp QC."
                        rows={data.floorLaborEfficiency.totals.unproductiveByStation}
                        emptyMessage="No unproductive station time in this window."
                      />
                    }
                  />
                </CardBody>
              </Card>
              <Card>
                <CardBody className="flex flex-col gap-1 px-3 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Untracked %</span>
                  <FloorLaborMetricValue
                    value={formatPercent(data.floorLaborEfficiency.totals.untrackedPercent)}
                    detail={
                      <>
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.untrackedMinutes)} untracked /{" "}
                        {formatMinutesAsHours(data.floorLaborEfficiency.totals.qbtPaidMinutes)} QBT paid
                      </>
                    }
                    hint={
                      <FloorLaborStationShareHoverHint
                        title="Untracked % by activity"
                        intro="QuickBooks Time paid time with no hub or mobile clock at all, as percentage points of QBT paid. Supervisor Daily task entries (Add misc time) are unproductive, not untracked."
                        rows={data.floorLaborEfficiency.totals.untrackedByActivity}
                        emptyMessage="Tracked hub and mobile time covers all QBT paid hours in this window."
                      />
                    }
                  />
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardBody className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Week</th>
                        <th className="px-4 py-3 text-right font-semibold">QBT paid</th>
                        <th className="px-4 py-3 text-right font-semibold">Billable</th>
                        <th className="px-4 py-3 text-right font-semibold">Billable %</th>
                        <th className="px-4 py-3 text-right font-semibold">Productive %</th>
                        <th className="px-4 py-3 text-right font-semibold">Unproductive %</th>
                        <th className="px-4 py-3 text-right font-semibold">Untracked %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.floorLaborEfficiency.weeks.map((row) => (
                        <tr key={row.startDate}>
                          <td className="px-4 py-3 whitespace-nowrap text-slate-900">
                            {formatReportDate(row.startDate)} – {formatReportDate(row.endDate)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            <HoverHintContent
                              hoverHint={
                                <FloorLaborPersonShareHoverHint
                                  title="QBT paid by person"
                                  intro="QuickBooks Time paid hours for the supervisor denominator pool in this week."
                                  rows={row.qbtPaidByPerson ?? []}
                                  emptyMessage="No QBT paid hours in this week."
                                />
                              }
                            >
                              <span className="tabular-nums">{formatMinutesAsHours(row.qbtPaidMinutes)}</span>
                            </HoverHintContent>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            <HoverHintContent
                              hoverHint={
                                <FloorLaborPersonShareHoverHint
                                  title="Billable by person"
                                  intro="Hub floor billable time in this week, by the operator who clocked it (all staff)."
                                  rows={row.billableByPerson ?? []}
                                  emptyMessage="No billable hub time in this week."
                                />
                              }
                            >
                              <span className="tabular-nums">{formatMinutesAsHours(row.billableMinutes)}</span>
                            </HoverHintContent>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                            {formatPercent(row.billablePercent)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                            {formatPercent(row.productivePercent)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                            {formatPercent(row.unproductivePercent)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                            {formatPercent(row.untrackedPercent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
            <p className="text-xs text-slate-500">
              QBT denominator: {data.floorLaborEfficiency.qbtDenominatorLabels.join(", ")}
            </p>
          </>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">Scheduled utilization</h2>
          <HoverHintWithGlyph hoverHint="Scheduled utilization is WO-attributed billable scan minutes divided by scheduled operator hours from the Production capacity roster. This is separate from billable % above, which uses QuickBooks Time paid supervisor hours as the denominator and includes clamp QC and shipment labor." />
        </div>

        {query.isLoading ? (
          <p className="py-6 text-center text-sm text-slate-500">Loading production cost report...</p>
        ) : query.error ? (
          <p className="py-6 text-center text-sm text-rose-700">{query.error.message}</p>
        ) : data == null ? (
          <p className="py-6 text-center text-sm text-slate-600">No production cost data is available.</p>
        ) : (
          <>
            <Card className="max-w-md">
              <CardBody className="flex flex-col gap-1 px-3 py-4">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Period utilization
                </span>
                <HoverHintContent
                  block
                  hoverHint={
                    <UtilizationStaffHoverHint staff={data.utilization.staff} targetPercent={targetPercent} />
                  }
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span
                      className={cn(
                        "text-2xl font-semibold tabular-nums",
                        utilizationTone(data.utilization.totals.utilizationPercent, targetPercent),
                      )}
                    >
                      {formatPercent(data.utilization.totals.utilizationPercent)}
                    </span>
                    <span className="text-sm text-slate-500">target {formatPercent(targetPercent)}</span>
                  </div>
                  <span className="text-xs text-slate-500">
                    Billable {formatMinutesAsHours(data.utilization.totals.billableMinutes)} / scheduled{" "}
                    {formatMinutesAsHours(data.utilization.totals.scheduledMinutes)}
                  </span>
                </HoverHintContent>
              </CardBody>
            </Card>

            <Card>
              <CardBody className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Day</th>
                        <th className="px-4 py-3 text-right font-semibold">Scheduled</th>
                        <th className="px-4 py-3 text-right font-semibold">Billable</th>
                        <th className="px-4 py-3 text-right font-semibold">Utilization</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {utilizationRows.map((row) => (
                        <tr key={row.date}>
                          <td className="px-4 py-3 whitespace-nowrap text-slate-900">{formatReportDate(row.date)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {formatMinutesAsHours(row.scheduledMinutes)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {formatMinutesAsHours(row.billableMinutes)}
                          </td>
                          <td
                            className={cn(
                              "px-4 py-3 text-right tabular-nums font-medium",
                              utilizationTone(row.utilizationPercent, targetPercent),
                            )}
                          >
                            {formatPercent(row.utilizationPercent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">Unit production cost</h2>
              <HoverHintWithGlyph hoverHint="Average Kodiak and SGS cost per serialized unit for each product family in the selected window. Material is the unweighted average of nonzero Kodiak ALT TLA Megaventory unit costs for that line (FI-3C A and C, FI-5A ALT TLA, and later letters when they have cost). That already includes TLA labor, TLA BoM, PCBA labor, and PCBA BoM. Hover a material cell for the exploded composition (not an extra sum). Clamp QC uses purchased coated-clamp finished-good unit cost. Suntronic equivalent material and labor are excluded and can be added to this report later. Station costs are SGS floor minutes valued at monthly internal labor plus overhead rates." />
            </div>
            <p className="text-sm text-slate-600">
              Kodiak and SGS costs only. Suntronic equivalent material and labor are not included yet.
            </p>
          </div>
          {data?.laborRates ? <LaborRateHeaderBadge laborRates={data.laborRates} /> : null}
        </div>

        {query.isLoading ? (
          <p className="py-4 text-center text-sm text-slate-500">Loading unit labor costs...</p>
        ) : query.error ? (
          <p className="py-4 text-center text-sm text-rose-700">{query.error.message}</p>
        ) : productLines.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-600">No product lines in this window.</p>
        ) : (
          <>
            {bomQuery.isError ? (
              <p className="text-sm text-rose-700">
                Kodiak / SGS material BoM costs failed to load: {bomQuery.error.message}
              </p>
            ) : bomMaterialPending ? (
              <p className="text-xs text-slate-500">Loading Kodiak / SGS TLA material costs...</p>
            ) : null}
            {productLines.map((line) => (
              <Card key={line.key}>
                <CardHeader>
                  <CardTitle className="text-base">{line.label}</CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  {!line.laborRatesConfigured ? (
                    <p className="border-b border-slate-100 px-4 py-3 text-sm text-amber-800">
                      Internal labor rates are missing for at least one month with scans in this window. Set rates in
                      Manufacturing → Settings.
                    </p>
                  ) : null}
                  {!line.bomMaterialPending && line.bomIssues.length > 0 ? (
                    <div className="space-y-2 border-b border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                      <p className="font-medium">Hub BoM needs attention for one or more SKUs in this window.</p>
                      <ul className="list-disc space-y-1 pl-5">
                        {line.bomIssues.map((issue) => (
                          <li key={`${issue.sku}:${issue.message}`}>
                            <Link
                              to={buildSetBomRulesDeepLink(issue.sku, { tab: "parse" })}
                              className="font-medium text-amber-950 underline decoration-amber-400 underline-offset-2 hover:text-amber-900"
                            >
                              {issue.sku}
                            </Link>
                            <span className="text-amber-900">: {issue.message}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Kodiak / SGS BOM</th>
                          {line.stations.map((station) => (
                            <th key={station.key} className="px-4 py-3 text-right font-semibold">
                              Station {station.stationNumber}
                            </th>
                          ))}
                          <th className="px-4 py-3 text-right font-semibold">SGS labor</th>
                          <th className="px-4 py-3 text-right font-semibold">Total cost</th>
                        </tr>
                        <tr className="bg-slate-50 text-[11px] font-normal normal-case tracking-normal text-slate-500">
                          <th className="px-4 pb-3 font-normal">TLA material</th>
                          {line.stations.map((station) => (
                            <th key={station.key} className="px-4 pb-3 text-right font-normal">
                              {station.label}
                            </th>
                          ))}
                          <th className="px-4 pb-3 text-right font-normal">Floor stations</th>
                          <th className="px-4 pb-3 text-right font-normal">Kodiak / SGS BOM + labor</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-4 py-3">
                            <CostCell
                              value={line.bomCostPerUnit}
                              pending={line.bomMaterialPending}
                              hint={<BomCostHoverHint line={line} />}
                            />
                          </td>
                          {line.stations.map((station) => (
                            <td key={station.key} className="px-4 py-3 text-right">
                              <CostCell value={station.costPerUnit} hint={station.timeBreakdown} />
                            </td>
                          ))}
                          <td className="px-4 py-3 text-right">
                            <CostCell
                              value={line.laborCostPerUnit}
                              hint={
                                line.laborCostPerUnit != null
                                  ? `SGS floor labor. Sum of station labor costs: ${line.stations
                                      .filter((station) => station.costPerUnit != null)
                                      .map(
                                        (station) =>
                                          `${station.label} ${formatCurrency(station.costPerUnit)}`,
                                      )
                                      .join(" + ")}.`
                                  : "Internal labor rates are missing for at least one month with scans in this window."
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CostCell
                              value={line.totalCostPerUnit}
                              pending={line.bomMaterialPending}
                              hint={`Kodiak / SGS TLA material ${formatCurrency(line.bomCostPerUnit)} plus SGS labor subtotal ${formatCurrency(line.laborCostPerUnit)} for the selected window. Suntronic equivalent costs are not included.`}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
